import React, { useState } from "react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Calculator } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { fmtMoney, track } from "../../lib/clarity";

const FIELDS = [
  ["Starting balances", [["taxable", "Taxable", 50000], ["deferred", "Tax-deferred", 250000], ["free", "Tax-free", 25000]]],
  ["Annual contributions", [["contrib_taxable", "Taxable", 2000], ["contrib_deferred", "Tax-deferred", 12000], ["contrib_free", "Tax-free", 6000]]],
  ["Growth assumptions (% / yr)", [["growth_taxable", "Taxable", 5], ["growth_deferred", "Tax-deferred", 5], ["growth_free", "Tax-free", 5], ["fee_pct", "Fees / cost drag", 0.5]]],
  ["Timeline & income", [["years_to_retirement", "Years until retirement", 15], ["retirement_years", "Years in retirement", 25], ["annual_net_need", "After-tax income need (today's $)", 60000], ["inflation", "Inflation %", 2.5]]],
  ["Tax assumptions (%)", [["ordinary_tax_rate", "Tax rate on deferred withdrawals", 22], ["cap_gains_rate", "Capital-gains rate", 15], ["taxable_gain_share", "Share of taxable withdrawals that is gain", 50]]],
];
const DEFAULTS = Object.fromEntries(FIELDS.flatMap(([, f]) => f.map(([k, , v]) => [k, v])));
const LABEL = { taxable: "Taxable", deferred: "Tax-deferred", free: "Tax-free" };
const COLORS = { taxable: "#9CA3AF", deferred: "#C5A059", free: "#34D399" };
const k$ = (v) => (Math.abs(v) >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : `$${Math.round(v / 1000)}K`);

function Results({ res, years }) {
  const ar = res.at_retirement;
  const taxes = res.strategies.map((s) => s.total_tax);
  const same = new Set(res.strategies.map((s) => s.years_fully_funded)).size === 1 && Math.max(...taxes) - Math.min(...taxes) <= 0.03 * Math.max(1, ...taxes);
  return (
    <div data-testid="illustrator-results" className="space-y-8 mt-10">
      <div className="grid md:grid-cols-3 gap-4">
        {["taxable", "deferred", "free"].map((k) => (
          <div key={k} data-testid={`illus-bucket-${k}`} className="card-surface p-5">
            <p className="eyebrow text-[10px]" style={{ color: COLORS[k] }}>{LABEL[k]} at retirement</p>
            <p className="font-display text-3xl mt-2">{fmtMoney(ar.pre_tax[k])}</p>
            <p className="text-xs text-exodus-muted mt-2">Est. embedded tax: {fmtMoney(ar.estimated_embedded_tax[k])}</p>
            <p className="text-xs text-exodus-text">After-tax equivalent: {fmtMoney(ar.after_tax_equivalent[k])}</p>
          </div>
        ))}
      </div>
      <p data-testid="illus-totals" className="text-sm text-exodus-muted">Total before tax <span className="text-exodus-text">{fmtMoney(ar.total_pre_tax)}</span> · estimated after-tax equivalent <span className="gold-text">{fmtMoney(ar.total_after_tax)}</span></p>
      <div className="card-surface p-4 sm:p-6 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={res.accumulation}>
            <CartesianGrid stroke="#262626" vertical={false} />
            <XAxis dataKey="year" stroke="#6B7280" fontSize={11} />
            <YAxis tickFormatter={k$} stroke="#6B7280" fontSize={11} width={60} />
            <Tooltip formatter={(v, n) => [fmtMoney(v), LABEL[n]]} contentStyle={{ background: "#141414", border: "1px solid #262626" }} />
            {["taxable", "deferred", "free"].map((k) => <Area key={k} type="monotone" dataKey={k} stackId="1" stroke={COLORS[k]} fill={COLORS[k]} fillOpacity={0.35} />)}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div>
        <p className="eyebrow mb-4">Hypothetical withdrawal orders compared</p>
        <div className="grid md:grid-cols-3 gap-4">
          {res.strategies.map((s) => (
            <div key={s.key} data-testid={`illus-strategy-${s.key}`} className="border border-exodus-border p-5">
              <p className="text-sm text-exodus-text">{s.label}</p>
              <p className="font-display text-3xl gold-text mt-3">{s.years_fully_funded} <span className="text-lg text-exodus-muted">of {years} yrs</span></p>
              <p className="text-xs text-exodus-muted">fully funded at your income need</p>
              <p className="text-xs text-exodus-muted mt-3">Estimated taxes paid: <span className="text-exodus-text">{fmtMoney(s.total_tax)}</span></p>
              <p className="text-xs text-exodus-muted">After-tax income delivered: <span className="text-exodus-text">{fmtMoney(s.total_after_tax_income)}</span></p>
            </div>
          ))}
        </div>
        {same && <p data-testid="illus-same-note" className="text-xs text-exodus-muted mt-4 max-w-3xl">With these inputs the withdrawal orders produce nearly the same result. In this flat-rate model, order matters mainly when growth rates or tax rates differ between buckets — real-world differences usually come from managing progressive tax brackets, which this simplified illustration does not capture.</p>}
      </div>
      <div className="border border-exodus-border p-5">
        <p className="eyebrow text-[10px] mb-3">Assumptions that drive this illustration</p>
        <ul className="list-disc pl-5 space-y-1 text-xs text-exodus-muted">{res.assumptions.map((a) => <li key={a}>{a}</li>)}</ul>
        <p data-testid="illus-disclaimer" className="text-xs text-exodus-text/80 mt-4">{res.disclaimer}</p>
      </div>
    </div>
  );
}

export default function TaxIllustrator() {
  const [f, setF] = useState(DEFAULTS);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, Number(v) || 0]));
      const r = await api.post("/tax-buckets/illustrate", body);
      setRes(r.data); track("illustrator_run");
    } catch (e) {
      toast.error("Please check your inputs — one or more values are out of range.");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="tax-illustrator" className="card-surface p-6 sm:p-10">
      <p className="eyebrow flex items-center gap-2"><Calculator size={14} /> Tax-Diversification Illustrator</p>
      <h2 className="font-display text-3xl sm:text-4xl mt-3 mb-2">See how tax treatment could shape retirement cash flow.</h2>
      <p className="text-sm text-exodus-muted max-w-2xl mb-10">Enter hypothetical figures. This is a simplified, flat-rate illustration — not a comprehensive retirement plan, and changing account types does not automatically produce tax savings.</p>
      <div className="grid lg:grid-cols-2 gap-x-10 gap-y-8">
        {FIELDS.map(([group, fields]) => (
          <fieldset key={group}>
            <legend className="eyebrow text-[10px] text-exodus-muted mb-3">{group}</legend>
            <div className="grid sm:grid-cols-2 gap-3">
              {fields.map(([k, label]) => (
                <label key={k} className="block">
                  <span className="text-xs text-exodus-muted">{label}</span>
                  <input data-testid={`illus-input-${k}`} type="number" inputMode="decimal" className="input-exodus mt-1" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>
      <button data-testid="illus-run-btn" onClick={run} disabled={busy} className="btn-gold mt-10">{busy ? "Calculating…" : "Run illustration"}</button>
      {res && <Results res={res} years={res.strategies[0].cash_flow.length} />}
    </section>
  );
}
